/***
 * Created on 2025/5/15
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useReducer, useState, useRef} from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import {useTranslation} from "react-i18next";
import TableComponent from "../common/TableComponent/TableComponent";
import {dataCellLabelToDataGridArea, normalizeSpaceSize} from "../../service/utils";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {invalidateAccessGrant} from "../../service/accessGrantSync";
import {useOrganization} from "../../context/OrganizationContext";
import {useAlertContext} from "../../context/AlertContext";
import ic_info_inline from '../../static/img/utils/ic-info-inline.svg';
import ic_sys_info_dark from "../../static/img/utils/ic-sys-info--dark.svg";
import {getTimeWithTimezone} from "../../service/timezone";

/**
 * §3.7 Revoke Case 1 (Action #23): is `userId` the only seated Owner in `region`?
 * `members` = records from Q2 `org_storage/{oid}/user`, which already carry both
 * `is_active_org_owner` (owner identity) and `seated_regions` (seat per region) —
 * so this needs no new Q2 field. Also accepts org-center `is_owner`/`role`.
 */
export const isLastSeatedOwnerInRegion = (members, region, userId) => {
    const seatedOwners = (members || []).filter(m =>
        (m?.is_owner || m?.is_active_org_owner || m?.role === 'owner') &&
        m?.seated_regions && Object.prototype.hasOwnProperty.call(m.seated_regions, region)
    );
    return seatedOwners.length === 1 &&
        (seatedOwners[0]?.id === userId || seatedOwners[0]?.user_id === userId);
};

const reducerHandler = (state, action) => {
    switch (action.type) {
        case 'select_all':
            const selected = state.data.map((item) => {item.checked = true; return item});
            return {
                ...state,
                data: selected,
                select_box: true,
                input: state.input + 1
            }
        case 'select_none':
            const unSelected = state.data.map((item) => {item.checked = false; return item});
            return {
                ...state,
                data: unSelected,
                select_box: false,
                input: state.input + 1
            }
        case 'toggle_checkbox':
            console.log(action.payload);
            const updatedData = state.data.map((item) => {
                if (item.space_id=== action.payload.space_id) {
                    return {...item, checked: !item.checked};
                }
                return item;
            });
            let check_status = updatedData?.every((o) => o.checked);
            if (!check_status) {
                check_status = updatedData?.some((o) => o.checked) ? 'partial' : false;
            }
            return {
                ...state,
                data: updatedData,
                select_box: check_status,
                input: state.input + 1
            }
        case 'update_data':
            return {
                ...state,
                data: action.payload,
                select_box: false,
                input: 1
            }
        case 'update_change_list':
            return {
                ...state,
                change_list: {...state?.change_list, ...action.payload},
                input: state.input + 1
            }
        default:
            return state;
    }
}

function ManageSpaceAccessEmptyList({t}) {
    return (
        <div className="flexbox-tbody">
            <div className="no-data-screen">
                <div className="no-data-info">
                    <div className="no-data-desc">
                        <div className="desc-title">{t('@Empty Spaces@')}</div>
                        <div className="desc-msg">{}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const RevokeOrgUerSpaceRow = ({table_head_fields, row_data, index, doRefresh, disPatcher, table_ui, isOwner, permissions, show_checkbox=false, translate:t}) => {
    const seat_assigned_to_user = Object.keys(row_data?.row_data?.seated_regions || []).find(r=>r===row_data.region);
    const [toggle, setToggle] = useState(seat_assigned_to_user);
    const hasSeats = row_data?.seats?.total - row_data?.seats?.used > 0;
    const is_owner = row_data.row_data.role === 'owner' || row_data.row_data.is_owner;
    const disable_revoke_last_owner = !!row_data?.is_last_seated_owner; // §3.7 Case 1 (#23): last seated Owner cannot revoke at click-time
    const onToggleChanged = () => {
        if (disable_revoke_last_owner || (!is_owner && !row_data.has_seated_owner) || (!hasSeats && !seat_assigned_to_user)) return;
        setToggle(prev=> {
            const newVal = !prev;
            disPatcher({type:'update_change_list', payload: {[row_data.region]: newVal}});
            return newVal;
        })
    }

    return (
        <div className="flexbox-trow" key={row_data.region}>
            <div className={`tbody-cell ${table_head_fields[0].width_style}`} data-cell-label={t(table_head_fields[0].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[0].field_name)}>
                {!is_owner && !row_data.has_seated_owner &&
                    <div className="display-flex align-items-center flex-gap-4">
                        <span className="text-ellipsis">{t(`@${row_data?.region}@`)}</span>
                        {!seat_assigned_to_user &&
                            <span className="flex-min-width-fit-content w-16px h-16px line-height-0 ml-4 tooltip">
                                <img className="display-block w-100 h-100" src={ic_info_inline}/>
                                <span className="tooltiptext !transform !top-0 !left-100 !-translate-y-20">{t('@Assign an organization owner to this region before adding seats for members.@')}</span>
                            </span>
                        }
                    </div>
                }
                {hasSeats && !(!is_owner && !row_data.has_seated_owner) && t(`@${row_data?.region}@`)}
                {!hasSeats && !(!is_owner && !row_data.has_seated_owner) &&
                    <div className="display-flex align-items-center flex-gap-4">
                        <span className="text-ellipsis">{t(`@${row_data?.region}@`)}</span>
                        {!seat_assigned_to_user &&
                            <span className="flex-min-width-fit-content w-16px h-16px line-height-0 ml-4 tooltip">
                                <img className="w-100" src={ic_info_inline}/>
                                <span className="tooltiptext">{t('@No available seats. Upgrade your plan to assign this user.@')}</span>
                            </span>
                        }
                    </div>
                }
            </div>
            <div className={`tbody-cell ${(!is_owner && !row_data.has_seated_owner) || (!hasSeats && !seat_assigned_to_user) ? 'tbody-cell--disabled' : ''} ${table_head_fields[2].width_style}`} data-cell-label={t(table_head_fields[2].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[2].field_name)}>
                {/*{normalizeSpaceSize(row_data?.usage)}*/}
                {row_data?.usage ? normalizeSpaceSize(row_data?.usage) : (row_data?.row_data?.seated_regions?.[row_data.region] ? normalizeSpaceSize(0) : '-')}
            </div>
            <div className={`tbody-cell ${(!is_owner && !row_data.has_seated_owner) || (!hasSeats && !seat_assigned_to_user) ? 'tbody-cell--disabled' : ''} ${table_head_fields[1].width_style}`} data-cell-label={t(table_head_fields[1].field_name)}
                 data-grid-area={dataCellLabelToDataGridArea(table_head_fields[1].field_name)}>
                {getTimeWithTimezone(row_data?.assigned_date, false)}
            </div>
            <div className={`tbody-cell ${table_head_fields[3].width_style}`} data-cell-label={t(table_head_fields[3].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[3].field_name)}>
                <div className="comp-switch">
                    <div className={`q-switch switch-${toggle ? 'on' : 'off'}`} onClick={onToggleChanged} disabled={disable_revoke_last_owner || (!is_owner && !row_data.has_seated_owner) || (!hasSeats && !seat_assigned_to_user)}></div>
                    {disable_revoke_last_owner &&
                        <span className="flex-min-width-fit-content w-16px h-16px line-height-0 ml-4 tooltip">
                            <img className="display-block w-100 h-100" src={ic_info_inline}/>
                            <span className="tooltiptext !transform !top-0 !left-100 !-translate-y-20">{t('@Assignment blocked: at least one Organization Owner must hold a storage seat. Add an Owner to the selection to continue.@')}</span>
                        </span>
                    }
                </div>
            </div>
        </div>
    );
}

function AccountListRevokeOrgUserSpaceModal({row_data, isOpen, onClose: closeModal, doRefresh}) {
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const org = getCurrentOrg();
    const alert = useAlertContext();
    const queryClient = useQueryClient();
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
    const modal = useRef(null);

    const table_head_fields = [
        {field_name: t('@Region@'), isSortable: true, width_style: '!w-33', field_id: 'region', default_sort: true, default_sort_order: ''},
        {field_name: t('@Usage by Region@'), isSortable: false, width_style: '!w-25', field_id: 'usage', info:{show_info: true, info_text: t('@Storage used in each region, including Personal and Shared Spaces.@')}},
        {field_name: t('@Assigned Date@'), isSortable: false, width_style: '!w-25', field_id: 'assigned_date'},
        {field_name: t('@Action@'), isSortable: false, width_style: '!w-17', field_id: 'usage'}
    ];

    const reducerInitializer = () => {
        return {
            data: [],
            input: 1,
            select_box: false
        }
    }

    const getUserRegionQuotaUsage = () => {
        let region_data = (row_data?.quota_usages || [])?.reduce((regionMap, region)=>{
            if (!regionMap.get(region.region)){
                regionMap.set(region.region, region)
            }
            else {
                regionMap.set(region.region, {
                    ...region,
                    usage: regionMap.get(region.region).usage + region.usage
                })
            }
            return regionMap;
        }, new Map());
        console.log('region_data', region_data);
        if (!region_data) {
            region_data = Object.keys(row_data.seated_regions).reduce((rm, rg) => {
                rm.set(rg, {
                    region: rg,
                    space_id: rg,
                    space_kind: 'org',
                    usage: 0
                })
                return rm;
            }, new Map());
        }
        console.log('region_data after reduce', Array.from(region_data?.values()));
        return Array.from(region_data?.values());
    }

    const {data, error, isError, isFetched} = useQuery({
        queryKey: ['cloud_space_index', org?.organization_id],
        queryFn: async () => {
            const response = await OrganizationAPI.q2.get(`org_storage/${org?.organization_id}/profile`)
            let space_profile = response.result;
            space_profile['data'] = space_profile?.region_infos;
            delete space_profile.region_infos;
            const user_region_data = getUserRegionQuotaUsage();
            // §3.7 Revoke Case 1 (#23): one unpaginated Q2 call carries owner flag + seated_regions
            // for every org user — enough to tell if this user is the last seated Owner per region.
            let org_seat_users = [];
            try {
                const seat_users_resp = await OrganizationAPI.q2.get(`org_storage/${org?.organization_id}/user`);
                org_seat_users = seat_users_resp?.result || [];
            } catch (e) {
                org_seat_users = [];
            }
            space_profile.data =  space_profile.data.map((item) => {
                return {
                    ...item,
                    row_data,
                    selected: false,
                    input: 1,
                    usage: user_region_data.find((region) => region.region === item.region)?.usage || null,
                    assigned_date: row_data?.seated_regions?.[item.region]?.assigned_at,
                    is_last_seated_owner: isLastSeatedOwnerInRegion(org_seat_users, item.region, row_data?.user_id),
                }
            });
            dispatch({type: 'update_data', payload: space_profile?.data});
            console.log('space_profile', space_profile.data);
            return space_profile;
        },
    });

    const revokeMutation = useMutation({
        mutationFn: async (data) => await OrganizationAPI.q2.patch(`org_storage/${org.organization_id}/user`, null, data),
        onSuccess: async (data, variables) => {
            let meta_data = {add: [], delete: []};
            for (const action of variables) {
                if (action.op === 'add') {
                    meta_data.add.push(action);
                }
                else if (action.op === 'delete') {
                    meta_data.delete.push(action);
                }
            }
            const activity_data = {
                organization_id: org.organization_id,
                user_id: currentUser.user_id,
                category: 6
            }
            if (meta_data.add.length>0) {
                await OrganizationAPI.put('organization_activity',null, {...activity_data, event: 22, meta_data: {data: meta_data.add}});
            }
            if (meta_data.delete.length>0) {
                await OrganizationAPI.put('organization_activity',null, {...activity_data, event: 23, meta_data: {data: meta_data.delete}});
            }
            setShowRevokeConfirm(false);
            doRefresh(prev => prev + 1);
            invalidateAccessGrant(queryClient, org.organization_id);
            closeModal();
            alert(true, 'success', 'ok',  t('@Access updated successfully for selected regions.@'), 10*1000);
        },
        onError: (err) => {
            console.log(err);
            const nestedCode = err?.response?.data?.errors?.[0]?.code;
            const code = nestedCode || err?.response?.data?.code;
            alert(true, 'error', code, t('@Failed to update access. Please try again.@'));
        }
    });

    const onConfirmAction = async () => {
        const payload = Object.entries(state?.change_list).map(([region, op]) => ({
            region,
            op: op ? 'add' : 'delete',
            user_id: row_data?.user_id,
            qid: row_data.email
        }));
        const has_delete_op = payload.some(p => p.op === 'delete');
        if (!showRevokeConfirm && has_delete_op) {
            setShowRevokeConfirm(true);
            modal.current.setSubmitText(t('@Confirm@'));
        } else {
            await revokeMutation.mutateAsync(payload);
        }
    }

    const handleClose = () => {
        if (showRevokeConfirm) {
            setShowRevokeConfirm(false);
        } else {
            closeModal();
        }
    }

    const [state, dispatch] = useReducer(reducerHandler, {}, reducerInitializer);

    const revoke_count = Object.values(state?.change_list || {}).filter((to_add) => !to_add).length;

    return (
        <ModalComponent
            title={showRevokeConfirm ? t('@{{revoke_count}} Members Will Lose Access@', {revoke_count}) : t('@Manage Organization Space Access@')}
            isOpen={isOpen}
            onClose={handleClose}
            onSubmit={onConfirmAction}
            okText={t('@Apply@')}
            input={state.input}
            preSubmit={(input)=>input && input > 1}
            ref={modal}>
            <div className="dialog-content">
                {showRevokeConfirm &&
                    <>
                        <div className="text-wrap">
                            {t('@You are about to revoke storage seats for the selected member(s).@')}
                        </div>
                        <div className="amount-info fs14 mb-16">
                            {t('@Their personal spaces will no longer be accessible to them. These personal spaces will be converted into Shared Spaces, allowing owners to continue managing the files.@')}
                        </div>
                        <div className="text-wrap">
                            {t('@Do you want to proceed?@')}
                        </div>
                    </>
                }
                {!showRevokeConfirm &&
                    <>
                        <div className="text-wrap">
                            {t('@If revoking a space access, the user’s Personal Space will be transferred to the organization and will only be visible to owners with assigned license seats.@')}
                        </div>
                        <div className="text-wrap">
                            {t('@Files in Shared Spaces will remain accessible if previously shared with other members. Unshared content will only be visible to owners.@')}
                        </div>

                        <TableComponent
                            table_head_fields={table_head_fields}
                            show_checkbox={true}
                            row_component={RevokeOrgUerSpaceRow}
                            table_data={state}
                            total_record={0}
                            doRefresh={() => {}}
                            disPatcher={dispatch}
                            table_ui={{table_field_checkbox: false}}
                            search={false}
                            isOwner={true}
                            createText={null}
                            permissions={null}
                            data_table_name="revoke-org-user-space-table"
                            css_with_context_wrapper={false}
                            pagination={false}
                            table_height="400px"
                            empty_data={<ManageSpaceAccessEmptyList t={t}/>}>
                        </TableComponent>
                    </>
                }
            </div>

        </ModalComponent>
    );
}

export default AccountListRevokeOrgUserSpaceModal;